import React from "react";
import { ChangeEvent } from "react";
import { Link } from "react-router-dom";
import { RouteComponentPropsHistory } from "../../types";

import DelayInput from "../DelayInput";

import { apiGet } from "../../Api";
import { /*formatDateTimeLocal,*/ formatDateTimeLocalNumeric } from "../../Utilities";
import { IAuthProps } from "../AuthContext";
// import HasRight from "../HasRight";
import withAuth from "../withAuth";
import withTitle from "../withTitle";

import "../../table.css";
import "../viewer.css";

interface IAppointmentPendingViewState {
    appointments: any[];
    appointmentsChanged: any[];
    locations: any[];

    searchText: string;
    searchLocation: string;
    searchPerson: string;

    isLoading: boolean | string;

    [name: string]: any;
}

class AppointmentPendingView extends React.Component<RouteComponentPropsHistory<any, any> & IAuthProps, IAppointmentPendingViewState> {
    private refreshTimer?: number;

    constructor(props: Readonly<RouteComponentPropsHistory<any, any> & IAuthProps>) {
        super(props);

        this.state = {
            appointments: [],
            appointmentsChanged: [],
            isLoading: true,

            locations: [],
            searchLocation: props.location.state ? props.location.state.searchLocation : props.authContext.user.employee.locationId,
            searchPerson: props.authContext.hasRight("appt:readall") ? "" : "caller",
            searchText: props.location.state ? props.location.state.searchText : "",
        };

        for (const appt of this.state.appointments) {
            appt.appointmentTimestamp = new Date(appt.appointmentTimestamp);
            appt.timestamp = new Date(appt.timestamp);
        }

        this.updateBackground = this.updateBackground.bind(this);
        this.handleElementChange = this.handleElementChange.bind(this);
    }

    public async componentDidMount() {
        try {
            const locations = await apiGet<any[]>("locations");

            this.setState({
                locations
            }, () => this.search());
        }
        catch (e) {
            this.setState({
                isLoading: e.message
            });
        }

        // TODO: Use a real-time websocket instead of polling
        this.refreshTimer = window.setInterval(this.updateBackground, 30 * 1000)
    }

    public componentWillUnmount() {
        if (this.refreshTimer) {
            window.clearInterval(this.refreshTimer);
        }
    }

    public render() {
        return (
            <div className="viewer-root">
                <div>
                    <div className="filter-control-box">
                        <DelayInput type="search" className="fill-space" placeholder="Search..." timeout={500} name="searchText" value={this.state.searchText} onChangeDelayed={this.handleElementChange} />
                        <select value={this.state.searchLocation} name="searchLocation" onChange={this.handleElementChange} disabled={this.state.isLoading === true}>
                            <option value="">All Locations</option>
                            {this.state.locations.map(a => <option key={a.id} value={a.id}>{a.shortName || a.name}</option>)}
                        </select>
                    </div>
                    <h3>Appointments Scheduled - Pending</h3>
                    <p>These appointments are not created yet and need to be entered into Prime Suite.</p>
                    {this.createTable(this.state.appointments)}
                    {!this.state.appointments || this.state.appointments.length === 0 ? <hr /> : ""}
                    <h3>Changed appointments</h3>
                    <p>These appointments have been changed and need to be updated in Prime Suite</p>
                    {this.createTable(this.state.appointmentsChanged)}
                </div>
            </div>
        );
    }

    private createTable(data: any[]) {
        let table;
        if (this.state.isLoading === true) {
            table = <div className="spinner-center lds-dual-ring" />;
        }
        else if (typeof this.state.isLoading === "string") {
            table = <span className="error-text">{this.state.isLoading}</span>;
        }
        else {
            if (data.length === 0) {
                table = <span>No appointments found</span>;
            }
            else {
                table = (
                    <table className="table-vertical">
                        <thead>
                            <tr>
                                <th>First Name</th>
                                <th>Last Name</th>
                                <th>Type</th>
                                <th>Location</th>
                                <th>Scheduled For</th>
                                <th>Created On</th>
                                <th className="col-smallest" />
                            </tr>
                        </thead>
                        <thead className="table-vertical-head-small">
                            <tr>
                                <th>Search results</th>
                            </tr>
                        </thead>
                        <tbody>
                            {
                                data.map(a => (
                                    <tr key={a.id}>
                                        <td data-caption="First Name: ">{a.patient.name.first}</td>
                                        <td data-caption="Last Name: ">{a.patient.name.last}</td>
                                        <td data-caption="Type: ">{a.appointmentType.name}</td>
                                        <td data-caption="Location: ">{a.location.name}</td>
                                        <td data-caption="Scheduled For: ">{formatDateTimeLocalNumeric(a.appointmentTimestamp)}</td>
                                        <td data-caption="Created On: ">{formatDateTimeLocalNumeric(a.timestamp)}</td>
                                        <td>
                                            <Link className="button" to={`/appointments/${a.id}`}>View Details</Link>
                                        </td>
                                    </tr>
                                ))
                            }
                        </tbody>
                    </table>
                );
            }
        }

        return table;
    }

    private async search(skipLoading?: boolean) {
        try {
            if (skipLoading !== true) {
                this.setState({
                    isLoading: true
                });
            }

            this.saveSearchState();

            const parameters: string[] = [
                `sort_by=date_for`
            ];
            if (this.state.searchText) {
                parameters.push(`q=${this.state.searchText}`);
            }
            if (this.state.searchLocation) {
                parameters.push(`location=${this.state.searchLocation}`);
            }
            if (this.state.searchPerson) {
                parameters.push(`person=${this.state.searchPerson}`);
            }

            const results = await apiGet<any[]>(`appointments/summary?state=changed&sort=asc&${parameters.join("&")}`);

            const appts = [];
            const changedAppts = [];

            for (const item of results) {
                if (item.status !== 0) {
                    changedAppts.push(item);
                }
                else {
                    appts.push(item);
                }
            }

            this.setState({
                appointments: appts,
                appointmentsChanged: changedAppts,
                isLoading: false
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message || "Failed to search"
            });
        }
    }

    private async updateBackground() {
        await this.search(true);
    }

    private handleElementChange(e: ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLInputElement>) {
        const value = e.currentTarget.value;
        const name = e.currentTarget.name;

        this.setState({
            [name]: value
        }, () => this.search());
    }

    private saveSearchState() {
        const location = this.props.location;
        location.state = {
            searchLocation: this.state.searchLocation,
            searchPerson: this.state.searchPerson,
            searchText: this.state.searchText,
        };

        this.props.history.replace(location);
    }
}

export default withAuth(withTitle("Pending Appointments", AppointmentPendingView));
